/**
 * Keyboard utilities for handling keyboard events
 * This file should remain dependency-free to avoid circular dependencies
 */

import { ValidationInputI } from '@msc-b2c/interfaces/types';

export const keyCodes = {
  ENTER: 'Enter',
  SPACE: ' ',
  END: 'End',
  HOME: 'Home',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_UP: 'ArrowUp',
  ARROW_RIGHT: 'ArrowRight',
  ARROW_DOWN: 'ArrowDown',
  TAB: 'Tab',
  ESCAPE: 'Escape',
};

/**
 * Handle keyboard events for accessibility (Enter/Space)
 * @param event - React keyboard event
 * @param action - Function to execute when Enter or Space is pressed
 * @param args - Optional arguments to pass to the action function
 * @param condition - Optional condition that must be true for the action to execute
 */
export const handleKeyDown = (
  event: React.KeyboardEvent,
  action: (...args: any[]) => void,
  args: any[] = [],
  condition = true
) => {
  if (condition && (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE)) {
    event.preventDefault();
    action(...args);
  }
};

/**
 * Get the keyboard type based on validation rules
 * @param validationRules - Array of validation rules for the input field
 * @returns "text" | "numeric" - The appropriate keyboard type for the input field
 */
export const getKeyboardType = (
  validationRules: ValidationInputI[] | undefined
): 'text' | 'numeric' => {
  if (!validationRules || validationRules.length === 0) return 'text';

  const numericValidation = new Set([
    'vr-only-numeric-min-3-digit',
    'vr-min-6-digit-and-max-13-digit',
  ]);
  const isNumeric = validationRules?.some((validation) =>
    numericValidation.has(validation.validationRules)
  );

  if (isNumeric) {
    return 'numeric';
  }

  return 'text';
};
